"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode, TouchEvent } from "react";
import Image from "next/image";
import ProductionHouseArt from "./ProductionHouseArt";
import ProductionHouseBackdrop from "./ProductionHouseBackdrop";

/**
 * Home hero — a three banner carousel (Figma Banner_0/1/2), running
 * Production House first on request (the design's own order was Banner_0 →
 * Banner_1 → Banner_2, i.e. the reverse of 1 and 3 below):
 *   1. Zero-Day & Exploit Production House  (node 2601:58399)
 *   2. Industrial Control System Security   (node 2601:58825)
 *   3. We Hack Things. Break Things
 *
 * The vertical three dot column the design puts to the left of the copy is the
 * slide indicator, so it doubles as the control on md and up; below md a
 * horizontal row sits at the bottom instead, because the column is hidden there.
 *
 * The column is a single, section level element pinned to the left margin and
 * vertically centred — one fixed spot on all three banners. It cannot stay inside
 * the copy row (where the design draws it) because each banner's copy sits at a
 * different height, and it cannot stay on the copy gutter either, because the
 * Production House diagram spans the full width from x=74 and the dots would land
 * on the SHRED lockup. Each copy row keeps a 20px spacer where the dots used to
 * be, so no headline moves.
 */

const SLIDE_TITLES = [
  "Zero-Day & Exploit Production House",
  "Industrial Control System Security",
  "We Hack Things. Break Things",
];
const SLIDE_COUNT = SLIDE_TITLES.length;
const AUTOPLAY_MS = 7000;

/* Every slide's headline sits on this gutter so it does not jump sideways during
   the cross fade. The Production House diagram is the one thing that opts out —
   it keeps the wider gutter Figma gives it. */
const COPY_GUTTER = "w-full px-4 sm:px-6 md:px-12 lg:pl-24 xl:pl-32 2xl:pl-48";
const HEADING = "font-avenir font-bold text-text-highlight tracking-normal break-words";

/**
 * The design's dot column: a 6px dot for every slide and a 20px ring around a red
 * core for the current one. Inactive dots stay 6px and get their hit area from a
 * transparent ::before, so the column keeps the exact 56px height (and therefore
 * the exact optical centre) whichever slide is active.
 *
 * `left` clears the Production House diagram, which starts at 74/1440 = 5.14% of
 * the hero: 34px back from that line leaves the 20px column plus 14px of air at every
 * width instead of only at 1440, and the 24px floor keeps the column off the
 * viewport edge at the narrow end of lg.
 */
function SlideRail({ active, onSelect }: { active: number; onSelect: (index: number) => void }) {
  return (
    <div className="absolute top-1/2 left-4 z-20 hidden -translate-y-1/2 flex-col items-center gap-3 md:flex lg:left-[max(24px,calc(5.14%_-_34px))]">
      {SLIDE_TITLES.map((title, index) =>
        index === active ? (
          <span key={title} className="relative flex h-5 w-5 items-center justify-center">
            <span className="absolute inset-0 rounded-full border border-primary" />
            <span className="block h-2 w-2 rounded-full bg-primary" />
          </span>
        ) : (
          <button
            key={title}
            type="button"
            onClick={() => onSelect(index)}
            aria-label={`Show slide ${index + 1}: ${title}`}
            className="relative block h-1.5 w-1.5 rounded-full bg-text-default/60 transition-colors before:absolute before:-inset-2 before:content-[''] hover:bg-text-default focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
          />
        )
      )}
    </div>
  );
}

/** Holds the gap the dot column used to occupy, so no headline shifts sideways. */
function RailSpacer({ className = "" }: { className?: string }) {
  return <div className={`hidden w-5 shrink-0 md:block ${className}`} aria-hidden="true" />;
}

export default function HomeHero() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  /* Slides 2 and 3 stay out of the DOM for the first moment so their full
     bleed photographs cannot compete with the first paint of slide 1.
     Autoplay only fires at 7s. */
  const [mountRest, setMountRest] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const go = useCallback((index: number) => {
    // A tap can beat the 1.2s deferral, and the target slide has to exist.
    setMountRest(true);
    setActive(((index % SLIDE_COUNT) + SLIDE_COUNT) % SLIDE_COUNT);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setMountRest(true), 1200);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (paused || !mountRest) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = window.setTimeout(
      () => setActive((current) => (current + 1) % SLIDE_COUNT),
      AUTOPLAY_MS
    );
    return () => window.clearTimeout(timer);
  }, [active, paused, mountRest]);

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "ArrowRight") go(active + 1);
    else if (event.key === "ArrowLeft") go(active - 1);
    else return;
    event.preventDefault();
  };

  const onTouchEnd = (event: TouchEvent) => {
    const start = touchStartX.current;
    touchStartX.current = null;
    if (start === null) return;
    const dx = event.changedTouches[0].clientX - start;
    if (Math.abs(dx) < 48) return;
    go(active + (dx < 0 ? 1 : -1));
  };

  return (
    <section
      /* The hero owns the whole first screen: the header is fixed and overlays
         it rather than taking layout height, so a full viewport here is exactly
         what fits, and the first product section only appears on scroll. The
         old height subtracted a 60px header that no longer occupies space, and
         the fixed 821px at lg left the next section peeking on tall screens.

         On phones 100vh counts the retracted URL bar, so the hero would
         overshoot the visual viewport and push the copy under the browser
         chrome. svh is the small-viewport unit; vh stays from sm up. */
      className="relative w-full h-[100svh] sm:h-screen min-h-[560px] md:min-h-[640px] overflow-hidden bg-background"
      aria-roledescription="carousel"
      aria-label="ECQ highlights"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={onKeyDown}
      onTouchStart={(event) => {
        touchStartX.current = event.touches[0].clientX;
      }}
      onTouchEnd={onTouchEnd}
    >
      <h1 className="sr-only">ECQ Cybersecurity Services and Products</h1>

      <SlideRail active={active} onSelect={go} />

      {/* ── 1. Zero-Day & Exploit Production House ────────────────────── */}
      <Slide index={0} active={active} title={SLIDE_TITLES[0]}>
        <ProductionHouseBackdrop />

        {/* Figma groups the headline and the diagram (node 2707:35623, at
            74/207, 1292.14 × 410.61) and centres that group in the 825px frame:
            the headline's 94px line box sits at y=207, the diagram band 76px
            under it at y=377, and the group's own centre lands on 412.3 against
            the frame's 412.5. So the column only has to centre — it no longer
            needs the 120px of top padding that used to push the diagram down to
            the y it had when the headline was below it.

            Below lg the padding is doing a different job: this is the one slide
            tall enough to reach the overlaid 60px header and the bottom slide
            dots, so it centres in the space between them.

            The design's 76px gap scales with the type rather than freezing: the
            headline is min(70px, 4.93vw), so the gap is the same ratio of the
            width, min(76px, 5.352vw) — 76/70 of it — and the two stop growing at
            the same width.

            The phone artboard (node 2709:38337, 390 × 844) leads on the
            headline too: its text box is 20/126, 349 × 96, and the diagram
            group follows at 20/256, 349.79 × 459.33 — 34px under it. That
            diagram is the same composition this file already draws, moved down
            103px on the artboard (SHRED 152.89 → 256, Q-Suite 584 → 687), so
            only the order and the gap change here, not the SVG.

            The 34px is used as the row gap even though the design's text box is
            96 tall against the 80 two 32/40 lines give: the box height follows
            from type this banner does not take from the design (that artboard
            still carries the wrong headline), while the gap is the design's own
            measure and holds whatever the type above it does. */}
        <div className="absolute inset-0 z-10 flex flex-col justify-center gap-[34px] pt-[60px] pb-12 md:pb-0 lg:gap-[min(76px,5.352vw)] lg:pt-0">
          {/* Spelled out rather than COPY_GUTTER because that constant's
              lg:pl-24 is a longhand Tailwind emits after px-*, so it would win
              over the diagram gutter at the same breakpoint. */}
          <div className="w-full px-4 sm:px-6 md:px-12 lg:px-[5.14%]">
            <div className="mx-auto flex w-full max-w-[1292px] items-start gap-4 md:gap-6">
              {/* At lg the dot column already sits outside the diagram gutter,
                  so the headline needs no spacer to clear it. */}
              <RailSpacer className="lg:hidden" />

              {/* Desktop keeps the whole phrase on one line. At the Figma 70px
                  it measures ~18.1em (1265px) wide, so from lg the size is
                  derived from the 89.72vw the gutter leaves (÷18.2 for a hair
                  of slack = 4.93vw) and capped at 70px, which the measure
                  clears from ~1420px up. The break stays below lg, where the
                  two line stack is unchanged.

                  Left aligned on the diagram's gutter, which is where the
                  design now puts it: Figma indents the text box a further 5px
                  (x=79 against the band's x=74) and pl carries that, so the
                  headline matches the design at 1440 and sits within 5px of the
                  diagram's own left edge at every other width. */}
              <div className="w-full text-[32px] sm:text-[40px] md:text-[56px] lg:pl-[5px] lg:text-[min(70px,4.93vw)]">
                <h2
                  className={`${HEADING} leading-[40px] sm:leading-[48px] md:leading-[72px] lg:whitespace-nowrap lg:leading-[1.343]`}
                >
                  Zero-Day &amp; Exploit <br className="lg:hidden" />
                  Production House
                </h2>
              </div>
            </div>
          </div>

          {/* From lg the diagram keeps its own, wider Figma gutter
              (74/1440 = 5.14%). Below lg it runs edge to edge: the phone
              artboard already carries its own 20px margins. */}
          <div className="w-full lg:px-[5.14%]">
            <div className="mx-auto w-full max-w-[1292px]">
              {/* The diagram animates its own pipeline, but only while this is
                  the banner on screen: all three slides stay in the DOM and
                  cross-fade, so an ungated loop would run under a hidden slide
                  and the viewer would only ever join it mid-cycle. */}
              <ProductionHouseArt playing={active === 0} />
            </div>
          </div>
        </div>
      </Slide>

      {/* ── 2. Industrial Control System Security ─────────────────────── */}
      {mountRest ? (
        <Slide index={1} active={active} title={SLIDE_TITLES[1]}>
          <div className="pointer-events-none absolute inset-0">
            <Image
              src="/images/Home/home-banner3.jpeg"
              alt="Dotted world map behind the outline of an industrial plant skyline"
              fill
              quality={85}
              sizes="100vw"
              className="object-cover object-center"
            />
          </div>

          <div className="absolute inset-0 z-10 flex items-center">
            <div className={COPY_GUTTER}>
              <div className="flex items-start gap-4 md:gap-6">
                <RailSpacer />

                <div className="max-w-2xl xl:max-w-3xl">
                  <h2
                    className={`${HEADING} text-[32px] leading-[40px] sm:text-[40px] sm:leading-[46px] md:text-[56px] md:leading-[64px] lg:text-[70px] lg:leading-[80px]`}
                  >
                    <span className="block">Industrial Control</span>
                    <span className="block">System Security</span>
                  </h2>
                  {/* Figma leaves 52px between the headline and the standfirst
                      on this banner, wider than the 32px the We Hack Things
                      banner uses. */}
                  <p className="mt-6 max-w-[420px] text-[16px] leading-[24px] text-text-highlight md:mt-8 lg:mt-[52px] lg:text-[19px] lg:leading-[25px]">
                    Simulate, attack, and remediate vulnerability of your critical infrastructure
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Slide>
      ) : null}

      {/* ── 3. We Hack Things. Break Things ───────────────────────────── */}
      {mountRest ? (
        <Slide index={2} active={active} title={SLIDE_TITLES[2]}>
          <div className="pointer-events-none absolute inset-0 mix-blend-lighten">
            <Image
              src="/images/Home/home-banner2.png"
              alt="Shattered fragments representing discovery and exploitation of vulnerabilities"
              fill
              quality={85}
              sizes="100vw"
              className="object-cover object-[75%_center] md:object-[70%_center] lg:object-[65%_center]"
            />
          </div>

          <div className="relative z-10 h-full">
            <div className="absolute inset-0 flex items-center">
              {/* px-4 below sm matches the site gutter convention (px-4 sm:px-6
                  lg:px-8) and the header, so the hero copy lines up with it. */}
              <div className={COPY_GUTTER}>
                <div className="flex items-start gap-4 md:gap-6">
                  <RailSpacer />

                  <div className="max-w-2xl xl:max-w-3xl">
                    {/* 32px below sm is the same mobile base as the Home section
                        headings; 40px onwards is unchanged. */}
                    <h2
                      className={`${HEADING} text-[32px] leading-[40px] sm:text-[40px] sm:leading-[48px] md:text-[56px] md:leading-[72px] lg:text-[70px] lg:leading-[94px]`}
                    >
                      <span className="block">We Hack Things.</span>
                      <span className="block">Break Things</span>
                    </h2>
                    <p className="body-1 mt-6 md:mt-8 text-text-default">
                      Discover, exploit, and mitigate vulnerability
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Slide>
      ) : null}

      {/* Below md the dot column is hidden, so the carousel gets a row here. */}
      <div className="absolute inset-x-0 bottom-5 z-20 flex justify-center gap-2.5 md:hidden">
        {SLIDE_TITLES.map((title, index) => (
          <button
            key={title}
            type="button"
            onClick={() => go(index)}
            aria-label={`Show slide ${index + 1}: ${title}`}
            aria-current={index === active}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              index === active ? "w-6 bg-primary" : "w-1.5 bg-text-default/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}

function Slide({
  index,
  active,
  title,
  children,
}: {
  index: number;
  active: number;
  title: string;
  children: ReactNode;
}) {
  const isActive = index === active;
  return (
    <div
      role="group"
      aria-roledescription="slide"
      aria-label={`${index + 1} of ${SLIDE_COUNT}: ${title}`}
      aria-hidden={!isActive}
      className={`absolute inset-0 transition-opacity duration-700 ease-out motion-reduce:transition-none ${
        isActive ? "opacity-100" : "pointer-events-none opacity-0"
      }`}
    >
      {children}
    </div>
  );
}
